<template>
  <div>
    <table class="table is-fullwidth">
      <thead>
        <tr>
          <th>State</th>
          <th>Kymeta Status</th>
          <th>Network Status</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="showSatelliteRows">
          <td>Sat Provisioned</td>
          <td>{{ (showSatelliteRows && terminal?.provisioned) ?? false ? 'Yes' : 'No'}}</td>
          <td :class="satProvisionedNetworkStatusColumnClass">{{ !!onNmsBeamIsInSyncWrtProv ? 'Yes' : 'No'}}</td>
        </tr>
        <tr v-if="terminal?.provisioned">
          <td colspan="3">
            <div class="detail-grid">
            <div></div>
            <div>
              <label class="has-text-weight-medium border-bottom">Required</label><br/>
              <div v-for="(kymBeam, kbixp) in kymetaBeams" :key='kbixp'>
                <p v-if="!!kymBeam.isRequired" :class="!!kymBeam.isRequired && isInOnNmsListColumn(kymBeam.networkId) ? '':'has-text-danger'"> {{ kymBeam.displayName }} </p>
              </div>
            </div>
            <div>
              <label class="has-text-weight-medium border-bottom">On NMS</label><br/>
              <div v-for="(remBeam, rbixp) in terminalBeams" :key='rbixp'>
                <p v-if="!!remBeam.provisionedOn" :class="isInRequiredListColumn(remBeam.networkId) ? '':'has-text-danger'"> {{ remBeam.displayName }}</p> 
              </div>
            </div>
          </div>
          </td>
        </tr>
        <tr v-if="showSatelliteRows">
          <td>Sat Activated</td>
          <td>{{ (showSatelliteRows && terminal?.activated) ?? false ? 'Yes' : 'No'}}</td>
          <td :class="satActivatedNetworkStatusColumnClass">{{ !!onNmsBeamIsInSyncWrtAct ? 'Yes' : 'No'}}</td>
        </tr>
        <tr v-if="terminal?.activated">
          <td colspan="3">
            <div class="detail-grid">
            <div></div>
            <div>
              <label class="has-text-weight-medium border-bottom">Required</label><br/>
              <div v-for="(kymBeam, kbixa) in kymetaBeams" :key='kbixa'>
                <p v-if="!!kymBeam.isRequired && !!terminal?.activated && !!(terminalBeams.length >= 0)" :class="!!kymBeam.isRequired && isInOnNmsListColumn(kymBeam.networkId)  ? '':'has-text-danger'"> {{ kymBeam.displayName }} </p>
              </div >
            </div>
            <div>
              <label class="has-text-weight-medium border-bottom">On NMS</label><br/>
              <div v-for="(remBeam, rbixa) in terminalBeams" :key='rbixa'>
                <p v-if="!!remBeam.activatedOn" :class="isInRequiredListColumn(remBeam.networkId) ? '':'has-text-danger'"> {{ remBeam.displayName }}</p> 
              </div>
            </div>
          </div> 
          </td>
        </tr>
        <tr v-if="showSatelliteRows && isCellHwAvailable">
          <td :class="!isCellHwAvailable ? 'has-text-grey' : ''">Cell Provisioned</td>
          <td>{{ terminal?.provisioned ?? false ? 'Yes' : 'No'}}</td>
          <td :class="showCellularRows && isCellHwAvailable ? 'has-text-success' : 'has-text-danger'">{{ cellProvisionedNetworkStatusColumnValue }}</td>
        </tr>
        <tr v-if="showSatelliteRows && isCellHwAvailable">
          <td :class="!isCellHwAvailable ? 'has-text-grey' : ''">Cell Activated</td>
          <td>{{ cellActivatedKymetaStatusColumnValue }}</td>
          <td :class="cellActivatedKymetaStatusColumnValue == cellActivatedNetworkStatusColumnValue ? 'has-text-success' : 'has-text-danger'">{{ cellActivatedNetworkStatusColumnValue }}</td>
        </tr>
        <tr v-if="showHybridRows && isHydHWAvailable">
          <td :class="!isHydHWAvailable ? 'has-text-grey' : ''">Hybrid Provisioned</td>
          <td>{{ terminal?.provisioned ?? false ? 'Yes' : 'No'}}</td>
          <td :class="hubState?.hybrid?.provisioned && isHydHWAvailable ? 'has-text-success' : 'has-text-danger'">{{ hubState?.hybrid?.provisioned && isHydHWAvailable ? 'Yes' : 'No'}}</td>
        </tr>
        <tr v-if="showHybridRows && isHydHWAvailable">
          <td :class="!isHydHWAvailable ? 'has-text-grey' : ''">Hybrid Activated</td>
          <td>{{ hydActivatedKymetaStatusColumnValue }}</td>
          <td :class="hydActivatedKymetaStatusColumnValue == hydActivatedNetworkStatusColumnValue ? 'has-text-success' : 'has-text-danger'">{{ hydActivatedNetworkStatusColumnValue }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script setup>
import { computed } from 'vue';
import { orderBy } from 'lodash';
import { useTerminalsStore } from '../terminals-store';

const store = useTerminalsStore();
const terminal = computed(() => store.state.terminal);
const hubState = computed(() => store.state.hubState);
const terminalBeams = computed(() => orderBy(hubState?.value?.kymetaBeams, ['name'], ['asc']));
const kymetaBeams = computed(() => orderBy(hubState?.value?.kymetaBeams, ['name'], ['asc']));
const reqKymetaBeams = computed(() => kymetaBeams?.value?.filter(kb => !!kb.isRequired));
const provisionedBeams = computed(() => terminalBeams?.value?.filter((pb) => pb?.provisionedOn));
const activatedBeams = computed(() => terminalBeams?.value?.filter((pb) => pb?.activatedOn));
const showSatelliteRows = computed(() => terminal?.value?.activeSubscription?.hasSatellite); // if active subs have hasSatellite = true
const showCellularRows = computed(() => terminal?.value?.activeSubscription?.hasCellular); // if active subs have hasCellular = true
const showHybridRows = computed(() => terminal?.value?.activeSubscription?.hasCellular); // if active subs have hasCellular = true
const isHydHWAvailable = computed(() => !!terminal?.value?.hardware?.hybridRouter?.imei || terminal?.value?.hardware?.hybridRouter?.serial);
const isCellHwAvailable = computed(() => !!terminal?.value?.hardware?.hybridRouter?.iccid || isHydHWAvailable.value);
const onNmsBeamIsInSyncWrtProv = computed(() => {
  // isRequired = true, Kymeta beams' list --- (List 1)
  const reqKymetaBeamsList1 = kymetaBeams?.value?.filter(kb => !!kb.isRequired)?.map(kb => kb.networkId);
  // early exit, if kymetaBeams not available for HW mismatch: antennaType, modemType
  if (reqKymetaBeamsList1?.length == 0) return false;
  // provisioned = true, Terminal's Beam list --- (List 2)
  const proTerminalBeamsList2 = terminalBeams?.value?.filter(rb => !!rb.provisionedOn)?.map(rb => rb.networkId);
  if (reqKymetaBeamsList1?.length > 0 && reqKymetaBeamsList1?.length != proTerminalBeamsList2?.length) return false;
  // if (List 2) length != 0, find difference in (List 1), (List 2) 
  return proTerminalBeamsList2?.length == 0 ? false : proTerminalBeamsList2?.filter(rb => !reqKymetaBeamsList1?.includes(rb))?.length == 0;
});
const onNmsBeamIsInSyncWrtAct = computed(() => {
  // isRequired = true, Kymeta beams' list --- (List 1)
  const reqKymetaBeamsList1 = kymetaBeams?.value?.filter(kb => !!kb.isRequired)?.map(kb => kb.networkId);
  // early exit, if kymetaBeams not available for HW mismatch: antennaType, modemType
  if (reqKymetaBeamsList1?.length == 0) return false;
  // activated = true, Terminal's Beam list --- (List 3)
  const actTerminalBeamsList2 = terminalBeams?.value?.filter(rb => !!rb.activatedOn)?.map(rb => rb.networkId);
  if (reqKymetaBeamsList1?.length > 0 && reqKymetaBeamsList1?.length != actTerminalBeamsList2?.length) return false;
  if (!terminal?.value?.activated && actTerminalBeamsList2?.length > 0) return false;
  // if (List 3) length != 0, find difference in (List 1), (List 3) 
  return actTerminalBeamsList2?.length == 0 ? false : !!actTerminalBeamsList2 ?? actTerminalBeamsList2?.filter(rb => !reqKymetaBeamsList1?.includes(rb))?.length == 0;
});
const satProvisionedNetworkStatusColumnClass = computed(() => {
  if (!terminal?.value?.provisioned && provisionedBeams?.value?.length == 0 && reqKymetaBeams?.value?.length == 0) return 'has-text-success';
  return onNmsBeamIsInSyncWrtProv?.value && terminal?.value?.provisioned ? 'has-text-success' : 'has-text-danger';
});
const satActivatedNetworkStatusColumnClass = computed(() => {
  if (!(terminal?.value?.activated) && activatedBeams?.value?.length == 0 && reqKymetaBeams.value?.length >= 0) return 'has-text-success';
  return onNmsBeamIsInSyncWrtAct?.value ? 'has-text-success' : 'has-text-danger'; 
});
const cellProvisionedNetworkStatusColumnValue = computed(() => (showCellularRows && isCellHwAvailable ? 'Yes' : 'No'));
const cellActivatedKymetaStatusColumnValue = computed(() => (terminal?.value?.activated && terminal?.value?.enableCellTraffic && showCellularRows && isCellHwAvailable ? 'Yes' : 'No'));
const cellActivatedNetworkStatusColumnValue = computed(() => (hubState?.value?.cellular?.activated ? 'Yes' : 'No'));
const hydActivatedKymetaStatusColumnValue = computed(() => (terminal?.value?.activated ? 'Yes' : 'No'));
const hydActivatedNetworkStatusColumnValue = computed(() => (hubState?.value?.hybrid?.activated && isHydHWAvailable ? 'Yes' : 'No'));
const isInOnNmsListColumn = (requiredBeamNetworkId) => terminalBeams?.value?.filter(b => b.networkId == requiredBeamNetworkId)?.length > 0;
const isInRequiredListColumn = (onNmsBeamNetworkId) => kymetaBeams?.value?.filter(b => b.networkId == onNmsBeamNetworkId && !!b.isRequired)?.length > 0
</script>
<style lang="scss" scoped>
@import "../../styles/mixins";
.detail-grid {
  @include display-grid;
  grid-template-columns: 1.23fr 1fr 1.05fr;
  gap: 5px;
  display: grid;
  position: relative;
}
.border-bottom {
  border-bottom: 1px solid gray;
}
</style>
